import { Button } from 'react-admin';
import React, { useMemo, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { ArrayElement, DefinitionType } from './constants/typedApiReturnTypes';
import {
  ConfigFieldByType,
  CollapsibleConfigSection,
  getDepth,
  getTitleFromKey,
  groupBySchemaSection,
} from './clientConfigSchemaField';
import { ClientConfigKeyOverridesControl } from '../components/clientConfig/ClientConfigKeyOverridesControl';
import { ClientConfigKeyRecord } from './clientConfigHelpers';

const indentBorderStyle = '2px solid #e0e0e0';

type GroupedDefinitions = {
  group: string;
  definitions: ArrayElement<DefinitionType>[];
}[];

const groupDefinitionsByTopKey = (definitions: DefinitionType): GroupedDefinitions => {

  const map = new Map<string, ArrayElement<DefinitionType>[]>();
  for (const definition of definitions) {

    const splitKey = definition.key.split('.')[0];
    const list = map.get(splitKey);
    if (list) {

      list.push(definition);

    } else {

      map.set(splitKey, [definition]);

    }

  }
  return Array.from(map.entries()).map(([group, defs]) => ({
    group,
    definitions: defs,
  }));

};

const createKeyMatchesSearch = (normalizedQuery: string) => (key: string) => {

  if (!normalizedQuery) {

    return false;

  }
  const normalizedKey = key.toLowerCase();
  return normalizedKey === normalizedQuery || normalizedKey.includes(normalizedQuery);

};

export const ClientConfigFormGenerator = (props: {
  definitions: DefinitionType;
  searchQuery?: string;
  keysByPath?: Record<string, ClientConfigKeyRecord>;
}) => {

  const { definitions, searchQuery = '', keysByPath } = props;
  const normalizedQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);
  const keyMatchesSearch = useMemo(() => createKeyMatchesSearch(normalizedQuery), [normalizedQuery]);

  const keyByGroup = useMemo(() => groupDefinitionsByTopKey(definitions), [definitions]);

  const depthByKey = useMemo(() => {

    const allKeys = definitions.map((def) => def.key);
    const map: Record<string, number> = {};
    definitions.forEach((def) => {

      map[def.key] = getDepth(def.key, allKeys);

    });
    return map;

  }, [definitions]);

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [mountedGroups, setMountedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (group: string) => {

    const wasCollapsed = collapsedGroups[group] ?? true;
    if (wasCollapsed) {

      setMountedGroups((prev) => ({

        ...prev,
        [group]: true,

      }));

    }
    setCollapsedGroups((prev) => ({

      ...prev,
      [group]: !(prev[group] ?? true),

    }));

  };

  return (
    <div>
      {keyByGroup.map((definition) => {

        const isCollapsed = collapsedGroups[definition.group] ?? true;
        const isMounted = mountedGroups[definition.group];
        const hitCount = normalizedQuery
          ? definition.definitions.reduce((count, def) => (
            keyMatchesSearch(def.key) ? count + 1 : count
          ), 0)
          : 0;
        return (
          <div
            key={definition.group}
            style={{
              borderRadius: '4px',
              padding: '1em',
              margin: '0 0 1em 0',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'white',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                marginBottom: '0.5em',
              }}
              onClick={() => toggleGroup(definition.group)}
              tabIndex={0}
              onKeyDown={(e) => {

                if (e.key === 'Enter' || e.key === ' ') toggleGroup(definition.group);

              }}
              aria-expanded={!isCollapsed}
              aria-controls={`group-content-${definition.group}`}
              role="button"
            >
              <PlayArrow
                sx={{
                  transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                  transition: 'transform 0.2s',
                  marginRight: '0.5em',
                }}
              />
              <h1 style={{ margin: 0, fontSize: '20px', order: 0 }}>
                {getTitleFromKey(definition.group)}
              </h1>
              {hitCount > 0 && (
                <Chip
                  label={hitCount}
                  size="small"
                  sx={{ marginLeft: '0.75em', backgroundColor: 'error.main', color: '#fff' }}
                />
              )}
            </div>
            {isMounted && (
              <div
                id={`group-content-${definition.group}`}
                style={{
                  display: isCollapsed ? 'none' : 'block',
                }}
              >
                {(() => {

                  const sections = groupBySchemaSection(definition.definitions, (def) => def.key, '');
                  const titledSectionCount = sections.filter((section) => section.title).length;
                  const defaultCollapsedSubSections = titledSectionCount !== 1;
                  return sections.map((section) => {

                    const sectionHitCount = normalizedQuery
                      ? section.items.reduce(
                        (count, def) => (keyMatchesSearch(def.key) ? count + 1 : count),
                        0,
                      )
                      : 0;
                    const renderDefinitionField = (def: ArrayElement<DefinitionType>) => {

                      const calcDepth = depthByKey[def.key] ?? 0;
                      const isSearchMatch = normalizedQuery.length > 0 && keyMatchesSearch(def.key);
                      return (
                        <div
                          style={{
                            paddingLeft: `${Math.max(calcDepth - 1, 0)}em`,
                            borderLeft: calcDepth > 1 ? indentBorderStyle : 'none',
                            order: def.schema.type === 'array' ? calcDepth + 1 : calcDepth,
                            ...(isSearchMatch ? {
                              backgroundColor: 'rgba(255, 235, 59, 0.35)',
                              borderRadius: '4px',
                              paddingTop: '0.25em',
                              paddingBottom: '0.25em',
                            } : {}),
                          }}
                          className={`margin-${calcDepth}`}
                          key={def.key}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <ConfigFieldByType definition={def} />
                            </Box>
                            {keysByPath && (
                              <ClientConfigKeyOverridesControl
                                configKey={def.key}
                                schema={def.schema}
                              />
                            )}
                          </Box>
                        </div>
                      );

                    };

                    if (!section.title) {

                      return section.items.map((def) => renderDefinitionField(def));

                    }
                    const sectionId = `${definition.group}::${section.title}`;
                    return (
                      <CollapsibleConfigSection
                        key={sectionId}
                        title={section.title}
                        defaultCollapsed={defaultCollapsedSubSections}
                        hitCount={sectionHitCount}
                      >
                        {section.items.map((def) => renderDefinitionField(def))}
                      </CollapsibleConfigSection>
                    );

                  });

                })()}
              </div>
            )}
          </div>
        );

      })}
      <Button type={'submit'} label={'ra.action.save'} variant={'contained'}/>
    </div>
  );

};
